import {
  Button,
  type ButtonVariant,
  Callout,
  type HighlightIconVariant,
  type IconName,
  Modal,
  Select,
} from '@dev-spendesk/grapes';
import { useState } from 'react';

import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import {
  NotificationType,
  useNotifications,
} from 'src/core/modules/app/notifications/hooks/useNotifications';
import { useOrganisationCompanyList } from 'src/core/modules/organisation-reporting/hooks/data/useOrganisationCompanyList';

import { useCompanyCostCenters } from '../../hooks/useCompanyCostCenters';
import { type CostCenterReplacePreview } from '../../hooks/useCostCenterReplacePreviewByCompany';
import { useReplaceCostCenterMutation } from '../../hooks/useReplaceCostCenterMutation';
import { type Setting } from '../../setting';

export const CostCenterReassignmentModal = ({
  iconVariant,
  iconName,
  title,
  submitButtonText,
  submitButtonVariant,
  subtitle,
  settings,
  companiesCostCentersToReplace,
  onCancel,
  onConfirm,
}: {
  iconVariant: HighlightIconVariant;
  iconName: IconName;
  title: string;
  submitButtonText: string;
  submitButtonVariant: ButtonVariant;
  subtitle: string;
  settings: Setting[] | Setting;
  companiesCostCentersToReplace: CostCenterReplacePreview[];
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const { pushNotif } = useNotifications();
  const [selectedCostCentersToReplace, setSelectedCostCentersToReplace] =
    useState<
      (
        | {
            costCenterIds: string[];
            companyId: string;
            costCenterToReplace: { id: string; name: string };
          }
        | undefined
      )[]
    >([]);
  const replaceCostCenter = useReplaceCostCenterMutation();
  const organisationCompanies = useOrganisationCompanyList();
  const companies = companiesCostCentersToReplace.map(
    (companyCostCenterToReplace) => ({
      id: companyCostCenterToReplace.companyId,
      name: organisationCompanies.find(
        (company) => company.id === companyCostCenterToReplace.companyId,
      )?.name,
    }),
  );

  const handleConfirm = async () => {
    try {
      await Promise.all(
        selectedCostCentersToReplace.flatMap((selectedCostCenterToReplace) => {
          if (!selectedCostCenterToReplace) {
            return [];
          }
          return selectedCostCenterToReplace.costCenterIds.map(
            async (costCenterId) =>
              replaceCostCenter.mutateAsync({
                costCenterId,
                companyId: selectedCostCenterToReplace.companyId,
                replacementId:
                  selectedCostCenterToReplace.costCenterToReplace.id,
              }),
          );
        }),
      );
    } catch (error) {
      pushNotif({
        type: NotificationType.Danger,
        message: t('multiEntityHub.costCenters.messages.errorDeleteCostCenter'),
      });
      throw error;
    }
    onConfirm();
  };

  const { t } = useTranslation('global');
  return (
    <Modal
      iconVariant={iconVariant}
      isOpen
      title={title}
      subtitle={subtitle}
      onClose={onCancel}
      iconName={iconName}
      actions={[
        <Button
          key="1"
          variant="secondaryNeutral"
          text={t('multiEntityHub.costCenters.reassign.actions.cancel')}
          onClick={onCancel}
        />,
        <Button
          key="2"
          variant={submitButtonVariant}
          text={submitButtonText}
          onClick={handleConfirm}
        />,
      ]}
    >
      <Callout
        variant="success"
        title={t('costCenters.deletion.alreadyExported')}
      />
      <div className="mt-24">
        {companies.map((company) => {
          const companyCostCenterToReplace = companiesCostCentersToReplace.find(
            (companyToReplace) => companyToReplace.companyId === company.id,
          );
          const costCenterIds = [settings]
            .flat()
            .flatMap((s) => s.entities || [])
            .filter((entity) => entity.companyId === company.id)
            .map((entity) => entity.costCenterId);

          if (!companyCostCenterToReplace || costCenterIds.length === 0) {
            return null;
          }
          return (
            <CompanyCard
              key={company.id}
              currentlyAssignedCostCenterIds={costCenterIds}
              onSelectCostCenterToReplace={(value) => {
                setSelectedCostCentersToReplace((previous) => {
                  const existingSelectedCostCenterToReplace = previous.find(
                    (selectedCostCenterToReplace) =>
                      selectedCostCenterToReplace?.companyId === company.id,
                  );
                  if (existingSelectedCostCenterToReplace) {
                    existingSelectedCostCenterToReplace.costCenterToReplace = {
                      id: value.id,
                      name: value.name,
                    };
                  } else {
                    previous.push({
                      costCenterIds,
                      companyId: company.id,
                      costCenterToReplace: {
                        id: value.id,
                        name: value.name,
                      },
                    });
                  }
                  return [...previous];
                });
              }}
              selectedCostCenterToReplace={selectedCostCentersToReplace.find(
                (selectedCostCenterToReplace) =>
                  selectedCostCenterToReplace?.companyId === company.id,
              )}
              company={{ id: company.id, name: company.name ?? '' }}
              companyCostCenterToReplace={companyCostCenterToReplace}
              showSeparator={
                companies.indexOf(company) !== companies.length - 1
              }
            />
          );
        })}
      </div>
    </Modal>
  );
};

const CompanyCard = ({
  company,
  companyCostCenterToReplace,
  currentlyAssignedCostCenterIds,
  onSelectCostCenterToReplace,
  selectedCostCenterToReplace,
  showSeparator,
}: {
  company: { id: string; name: string };
  companyCostCenterToReplace: CostCenterReplacePreview;
  currentlyAssignedCostCenterIds: string[];
  onSelectCostCenterToReplace: (costCenter: {
    id: string;
    name: string;
  }) => void;
  selectedCostCenterToReplace:
    | { companyId: string; costCenterToReplace: { id: string; name: string } }
    | undefined;
  showSeparator: boolean;
}) => {
  const { t } = useTranslation('global');
  const companyCostCentersQueryState = useCompanyCostCenters(company.id);

  if (companyCostCentersQueryState.status !== 'success') {
    return null;
  }

  const costCentersToReplace = companyCostCentersQueryState.data.filter(
    (cc: { id: string }) => !currentlyAssignedCostCenterIds.includes(cc.id),
  );

  return (
    <div className="mb-24 text-left">
      <div className="text-content-primary title-l mb-16">{company.name}</div>
      <Callout
        variant="warning"
        title={getCalloutTitle(companyCostCenterToReplace, t)}
      />
      {costCentersToReplace.length > 0 ? (
        <Select
          fit="parent"
          placeholder={t(
            'multiEntityHub.costCenters.reassign.selectCostCenter',
          )}
          options={costCentersToReplace.map(
            (cc: { id: string; name: string }) => ({
              key: cc.id,
              label: cc.name,
            }),
          )}
          className="mt-16"
          value={
            selectedCostCenterToReplace
              ? {
                  key: selectedCostCenterToReplace.costCenterToReplace.id,
                  label: selectedCostCenterToReplace.costCenterToReplace.name,
                }
              : undefined
          }
          onSelect={(value) => {
            if (value) {
              onSelectCostCenterToReplace({
                id: value.key,
                name: value.label,
              });
            }
          }}
        />
      ) : null}
      {showSeparator && <div className="separator mt-24" />}
    </div>
  );
};

const getCalloutTitle = (
  companyCostCenterToReplace: CostCenterReplacePreview,
  t: TGlobalFunctionTyped,
) => {
  const titles = Object.entries(companyCostCenterToReplace.preview)
    .filter(([_, value]) => value > 0)
    .map(([key, value]) => {
      if (key === 'approvedRequestsCount') {
        return t(
          'multiEntityHub.costCenters.reassign.replacements.approvedRequests',
          {
            count: value,
          },
        );
      }
      if (key === 'notReadyPaymentsCount') {
        return t(
          'multiEntityHub.costCenters.reassign.replacements.notReadyPayments',
          {
            count: value,
          },
        );
      }
      if (key === 'pendingRequestsCount') {
        return t(
          'multiEntityHub.costCenters.reassign.replacements.pendingRequests',
          {
            count: value,
          },
        );
      }
      if (key === 'purchaseOrdersCount') {
        return t(
          'multiEntityHub.costCenters.reassign.replacements.purchaseOrders',
          {
            count: value,
          },
        );
      }
      if (key === 'subscriptionsCount') {
        return t(
          'multiEntityHub.costCenters.reassign.replacements.subscriptions',
          {
            count: value,
          },
        );
      }
      if (key === 'usersCount') {
        return t('multiEntityHub.costCenters.reassign.replacements.users', {
          count: value,
        });
      }
      return undefined;
    });
  return titles.join(', ');
};
